import {
  getEncodingProfile,
  type EncodingSystem,
} from "./encodingProfiles";
import { wikipediaCacheKey } from "./lib/nummem";

type KnownNumberIdentity = {
  id: string;
  label: string;
};

type WikidataEntityResponse = {
  entities?: Record<string, {
    sitelinks?: Record<string, {
      site?: unknown;
      title?: unknown;
    }>;
  }>;
};

type WikidataSearchResponse = {
  search?: Array<{
    id?: unknown;
    match?: {
      language?: unknown;
      text?: unknown;
      type?: unknown;
    };
  }>;
};

const MAX_WIKIPEDIA_TITLE_LENGTH = 512;
const WIKIDATA_ENTITY_ID = /^Q[1-9][0-9]*$/;

export function knownNumberWikipediaCacheKey(
  encodingSystem: EncodingSystem,
  entry: KnownNumberIdentity,
): string {
  const resolution = getEncodingProfile(encodingSystem)
    .knownNumberWikipediaIdentity;
  return resolution.kind === "catalog-label"
    ? wikipediaCacheKey(encodingSystem, entry.label)
    : `${encodingSystem}:known-number:${encodeURIComponent(entry.id)}`;
}

export async function resolveKnownNumberWikipediaTitle(
  catalogLabel: string,
  encodingSystem: EncodingSystem,
  fetcher: typeof fetch = fetch,
): Promise<string | null> {
  const resolution = getEncodingProfile(encodingSystem)
    .knownNumberWikipediaIdentity;
  if (resolution.kind === "catalog-label") {
    return catalogLabel;
  }
  const normalizedLabel = catalogLabel.trim();
  if (!normalizedLabel) {
    return null;
  }
  try {
    const searchResponse = await fetcher(
      wikidataExactLabelSearchUrl(
        normalizedLabel,
        resolution.resolverHost,
        resolution.sourceLanguage,
      ),
      { headers: { accept: "application/json" } },
    );
    if (!searchResponse.ok) {
      return null;
    }
    const searchPayload = (await searchResponse.json()) as WikidataSearchResponse;
    const entityIds = new Set<string>();
    const expectedLabel = normalizedIdentityLabel(
      normalizedLabel,
      resolution.sourceLanguage,
    );
    for (const result of searchPayload.search ?? []) {
      if (
        typeof result.id === "string" &&
        WIKIDATA_ENTITY_ID.test(result.id) &&
        result.match?.type === "label" &&
        result.match.language === resolution.sourceLanguage &&
        typeof result.match.text === "string" &&
        normalizedIdentityLabel(
          result.match.text,
          resolution.sourceLanguage,
        ) === expectedLabel
      ) {
        entityIds.add(result.id);
      }
    }
    if (entityIds.size !== 1) {
      return null;
    }
    const entityId = [...entityIds][0];
    const response = await fetcher(
      wikidataSitelinkResolutionUrl(
        entityId,
        resolution.resolverHost,
        resolution.targetSite,
      ),
      { headers: { accept: "application/json" } },
    );
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as WikidataEntityResponse;
    const entity = payload.entities?.[entityId];
    const sitelink = entity?.sitelinks?.[resolution.targetSite];
    if (sitelink?.site !== resolution.targetSite) {
      return null;
    }
    return validWikipediaTitle(sitelink.title);
  } catch {
    return null;
  }
}

export function wikidataSitelinkResolutionUrl(
  entityId: string,
  resolverHost: string,
  targetSite: string,
): string {
  const parameters = new URLSearchParams({
    action: "wbgetentities",
    format: "json",
    formatversion: "2",
    ids: entityId,
    origin: "*",
    props: "sitelinks",
    sitefilter: targetSite,
  });
  return `https://${resolverHost}/w/api.php?${parameters.toString()}`;
}

export function wikidataExactLabelSearchUrl(
  catalogLabel: string,
  resolverHost: string,
  sourceLanguage: string,
): string {
  const parameters = new URLSearchParams({
    action: "wbsearchentities",
    format: "json",
    formatversion: "2",
    language: sourceLanguage,
    limit: "50",
    origin: "*",
    search: catalogLabel,
    strictlanguage: "1",
    type: "item",
    uselang: sourceLanguage,
  });
  return `https://${resolverHost}/w/api.php?${parameters.toString()}`;
}

function normalizedIdentityLabel(value: string, language: string): string {
  return value.trim().normalize("NFKC").toLocaleLowerCase(language);
}

function validWikipediaTitle(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const title = value.trim();
  return title &&
      title.length <= MAX_WIKIPEDIA_TITLE_LENGTH &&
      ![...title].some((character) => {
        const codePoint = character.codePointAt(0) ?? 0;
        return codePoint <= 31 || codePoint === 127;
      })
    ? title
    : null;
}
